import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import NotiController from '../../services/system/noti/noti-controller';

/**
 * Chuông thông báo cá nhân trên header (SCR-138).
 *
 * CMS trước đây KHÔNG có hộp thư cá nhân: màn `routes/admin/noti` là màn SOẠN & GỬI thông báo,
 * không phải chỗ đọc thông báo gửi cho mình. Hệ quả là mọi thông báo engine bắn ra (đơn xin nghỉ
 * chờ duyệt, duyệt/từ chối, khiếu nại chấm công…) chỉ tới được app di động, người ngồi CMS không
 * bao giờ thấy.
 *
 * Dùng nhóm `/notify/my/*` chứ KHÔNG dùng `notify/search`: bản `search` nhận `officerId` từ client
 * và không đối chiếu với người đang đăng nhập (xem contract `notify-search.md`), `officerId = -1`
 * thì trả về thông báo của cả trường và `isRead` luôn null.
 *
 * Lỗi API đều nuốt im (danh sách rỗng / badge 0): chuông chạy nền ở MỌI màn, bật toast là màn nào
 * cũng đỏ khi server chưa deploy nhóm `/my/*`.
 */

// Nhịp hỏi lại số chưa đọc. 60s đủ "gần thật" mà không biến chuông thành máy bơm request.
const POLL_MS = 60 * 1000;
const PREVIEW_SIZE = 10;

const fmtTime = (v) => {
    if (!v) return '';
    const m = moment(v);
    if (!m.isValid()) return '';
    const diffHours = moment().diff(m, 'hours');
    // Trong ngày thì xem "2 giờ trước" dễ đọc hơn mốc tuyệt đối; cũ hơn thì cần biết ngày.
    return diffHours < 24 ? m.fromNow() : m.format('DD/MM/YYYY HH:mm');
};

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);

    const rootRef = useRef(null);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const loadCount = useCallback(() => {
        NotiController.myUnreadCount((res) => {
            if (res && res.success && typeof res.data === 'number') {
                setUnread(res.data);
            }
            // Thất bại => giữ nguyên số cũ, không nhảy về 0 (tránh badge chớp tắt khi mạng chập).
        });
    }, []);

    useEffect(() => {
        loadCount();
        const timer = setInterval(loadCount, POLL_MS);
        return () => clearInterval(timer);
    }, [loadCount]);

    const loadList = useCallback(() => {
        setLoading(true);
        NotiController.myInbox(
            { pageNumber: 0, pageSize: PREVIEW_SIZE, sort: 'id desc' },
            (res) => {
                setLoading(false);
                const content = res && res.success && res.data ? res.data.content : null;
                setItems(Array.isArray(content) ? content : []);
            },
        );
    }, []);

    const toggle = (e) => {
        e.stopPropagation();
        setOpen((v) => {
            // Mở lại thì tải lại: khác "Xem nhanh" (danh sách tĩnh do user tự ghim), thông báo
            // thay đổi liên tục nên cache lần đầu là hiện dữ liệu cũ.
            if (!v) loadList();
            return !v;
        });
    };

    const isUnread = (it) => !(it.isRead === true || it.readNoti === true);

    const openItem = (it) => {
        setOpen(false);
        if (!isUnread(it)) return;
        // Đánh dấu đã đọc lạc quan: màn chi tiết cũng tự đánh dấu, nhưng badge phải giảm ngay
        // nếu không user bấm xong quay lại vẫn thấy số cũ và tưởng hỏng.
        setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, isRead: true, readNoti: true } : x)));
        setUnread((n) => (n > 0 ? n - 1 : 0));
        NotiController.myRead(it.id, () => {});
    };

    const markAll = () => {
        NotiController.myMarkAllRead((res) => {
            if (res && res.success) {
                setItems((prev) => prev.map((x) => ({ ...x, isRead: true, readNoti: true })));
                setUnread(0);
            }
        });
    };

    return (
        <div className={`notif-bell ${open ? 'open' : ''}`} ref={rootRef}>
            <button
                className="notif-bell-btn"
                onClick={toggle}
                type="button"
                title="Thông báo của tôi"
            >
                <i className="fas fa-bell" />
                {unread > 0 && (
                    <span className="notif-bell-badge">{unread > 99 ? '99+' : unread}</span>
                )}
            </button>

            <div className="notif-bell-menu">
                <div className="nb-header">
                    <span className="nb-title">
                        <i className="fas fa-bell" /> Thông báo
                    </span>
                    {unread > 0 && (
                        <button className="nb-markall" type="button" onClick={markAll}>
                            <i className="fas fa-check-double" /> Đánh dấu đã đọc
                        </button>
                    )}
                </div>

                <div className="nb-body">
                    {loading && <div className="nb-empty">Đang tải…</div>}
                    {!loading && items.length === 0 && (
                        <div className="nb-empty">Chưa có thông báo nào.</div>
                    )}
                    {!loading && items.map((it) => (
                        <Link
                            key={it.id}
                            className={`nb-item ${isUnread(it) ? 'unread' : ''}`}
                            to={`/ad-sas-school/thong-bao-cua-toi?id=${it.id}`}
                            onClick={() => openItem(it)}
                        >
                            <span
                                className="nb-item-icon"
                                style={it.categoryColor ? { background: it.categoryColor } : undefined}
                            >
                                <i className={it.categoryIcon || 'fas fa-info'} />
                            </span>
                            <span className="nb-item-main">
                                <span className="nb-item-title">{it.title}</span>
                                {it.shortDescription && (
                                    <span className="nb-item-desc">{it.shortDescription}</span>
                                )}
                                <span className="nb-item-time">{fmtTime(it.createdAt || it.dateActive)}</span>
                            </span>
                            {isUnread(it) && <span className="nb-item-dot" />}
                        </Link>
                    ))}
                </div>

                <div className="nb-footer">
                    <Link to="/ad-sas-school/thong-bao-cua-toi" onClick={() => setOpen(false)}>
                        Xem tất cả thông báo
                    </Link>
                </div>
            </div>
        </div>
    );
}
